import React, { useState, useEffect, useCallback } from 'react';
import employeeAttendanceAPI from '../api/employeeAttendanceAPI';
import { useEmployeeAttendanceAuth } from '../context/EmployeeAttendanceAuthContext';
import { getInitials } from '../utils/attendanceFormatters';
import { MdPerson, MdWork, MdPhone, MdMail, MdBadge } from 'react-icons/md';
import '../employeeAttendance.css';
import '../employeeAttendance-compact.css';

function EmployeeProfile() {
  const { getToken } = useEmployeeAttendanceAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProfile = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      const result = await employeeAttendanceAPI.getInfo(token);

      if (result?.success && result.profile) {
        setProfile(result.profile);
      } else {
        setError(result?.message || 'Failed to load profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  if (loading) {
    return (
      <div className="ea-main" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <p className="ea-page-sub">Loading profile…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ea-main" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: '340px' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>😕</div>
          <p className="ea-page-sub" style={{ color: '#991b1b', marginBottom: '8px', fontWeight: 500 }}>
            Unable to load your profile
          </p>
          <p className="ea-page-sub" style={{ color: '#6b7280', fontSize: '0.85rem' }}>
            {error}
          </p>
          <button type="button" className="ea-btn-submit mt-4" onClick={loadProfile}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const photoUrl = profile.image_url
    ? `${employeeAttendanceAPI.baseURL}${profile.image_url}`
    : null;

  const Section = ({ icon: Icon, title, children }) => (
    <div className="ea-profile-section">
      <h3 className="ea-profile-section-title">
        <Icon size={18} aria-hidden />
        {title}
      </h3>
      <div className="ea-profile-grid">{children}</div>
    </div>
  );

  const Field = ({ label, value, fallback = '—' }) => (
    <div className="ea-profile-field">
      <span className="ea-profile-label">{label}</span>
      <span className="ea-profile-value">{value || fallback}</span>
    </div>
  );

  return (
    <div className="ea-main">
      <div className="ea-topbar">
        <div>
          <div className="ea-greeting">My Profile</div>
          <div className="ea-dateline">Employee information</div>
        </div>
      </div>

      <div className="ea-profile-card">
        <div className="ea-profile-header">
          <div className="ea-profile-avatar">
            {photoUrl ? (
              <img src={photoUrl} alt={profile.name} className="ea-profile-img" />
            ) : (
              <span className="ea-profile-initials">{getInitials(profile.name)}</span>
            )}
          </div>
          <div className="ea-profile-name-block">
            <h2 className="ea-profile-name">{profile.name}</h2>
            <p className="ea-profile-job">{profile.job_title}</p>
            {profile.employee_no && (
              <p className="ea-profile-badge">
                <MdBadge size={14} aria-hidden />
                {profile.employee_no}
              </p>
            )}
          </div>
        </div>

        <Section icon={MdPerson} title="Personal Information">
          <Field label="Gender" value={profile.gender} />
          <Field label="Date of Birth" value={profile.birth_date} />
          <Field label="Marital Status" value={profile.marital_status} />
          <Field
            label="Nationality"
            value={
              profile.nationality
                ? `${profile.nationality}${profile.nationality_code ? ` (${profile.nationality_code})` : ''}`
                : null
            }
          />
          <Field label="Place of Birth" value={profile.place_of_birth} />
          <Field label="Passport ID" value={profile.passport_id} />
        </Section>

        <Section icon={MdWork} title="Work Information">
          <Field label="Job Title" value={profile.job_title} />
          <Field label="Department" value={profile.department_name} />
          <Field label="Manager" value={profile.manager_name} />
          <Field label="Coach" value={profile.coach_name} />
          <Field label="Work Location" value={profile.work_location} />
          <Field label="Company" value={profile.company_name} />
          <Field label="Working Hours" value={profile.working_hours_name} />
        </Section>

        <Section icon={MdMail} title="Contact Information">
          <Field label="Work Email" value={profile.work_email} />
          <Field label="Private Email" value={profile.private_email} />
          <Field label="Work Phone" value={profile.work_phone} />
          <Field label="Mobile Phone" value={profile.mobile_phone} />
        </Section>

        <Section icon={MdPhone} title="Emergency Contact">
          <Field label="Name" value={profile.emergency_contact} />
          <Field label="Phone" value={profile.emergency_phone} />
        </Section>
      </div>
    </div>
  );
}

export default EmployeeProfile;